import { localizedTitle } from "@/lib/i18n/ssrSeo";
import { seoFor } from "@/lib/i18n/keywordPools";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { getPublicUpcomingPicks, getDailyMemberPick } from "@/lib/publicPicks.functions";
import { hubJsonLd } from "@/lib/picksJsonLd";

const picksQuery = queryOptions({ queryKey: ["public-upcoming-picks"], queryFn: () => getPublicUpcomingPicks(), staleTime: 5 * 60_000 });
import Index from "@/pages/Index";
import { MainLayout } from "@/components/layout/MainLayout";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    const [picks] = await Promise.all([
      context.queryClient.ensureQueryData(picksQuery),
      context.queryClient.ensureQueryData({ queryKey: ["daily-member-pick"], queryFn: () => getDailyMemberPick(), staleTime: 5 * 60_000 }),
    ]);
    return picks;
  },
  head: ({ loaderData }) => {
    const L = localizedTitle("/", { title: "Statlign: AI Sports Predictions & Betting Tips Today", description: "Transparent, explainable sports picks from a 50-strategy AI engine. Every call ranked by conviction. Every reason on record. Free to inspect." });
    return {
    meta: [
      { name: "facebook-domain-verification", content: "lz6ibh9l9xc49p336gpm1d3y08jgtb" },
      { title: L.title },
      { name: "keywords", content: seoFor({ topics: ["predictions", "aiPredictions", "home", "nigeria"] }).meta },
      { name: "description", content: L.description },
      { property: "og:title", content: L.title },
      { property: "og:type", content: "website" },
      { property: "og:description", content: L.description },
      { name: "twitter:title", content: L.title },
      { name: "twitter:description", content: L.description },
    ],
    scripts: [
      ...hubJsonLd({ path: "/", name: "Statlign AI sports predictions", description: "Upcoming AI-analysed football picks with odds, confidence and reasoning.", picks: (loaderData ?? []) }),
      ...homeJsonLd(L.title, L.description),
    ],
  };
  },
  component: () => (
    <MainLayout>
      <Index />
    </MainLayout>
  ),
});

const HOME_FAQ: [string, string][] = [
  ["What is Statlign?", "Statlign is an AI-powered sports intelligence platform that publishes explainable football predictions, odds and confidence scores, with the reasoning behind every pick."],
  ["How does Statlign make predictions?", "Each match is scored by a 50-strategy engine using recent form, real bookmaker odds, goal patterns and match context. A pick is only published when enough strategies agree and both teams have at least five real results."],
  ["Are Statlign predictions free?", "Yes. Upcoming picks with their market, odds and confidence are free to view. Members get a free pick of the day and free reveals; deeper analysis and full access come with a subscription."],
  ["Does Statlign guarantee wins?", "No. No prediction is guaranteed. Statlign shows its verified track record so you can judge its accuracy, and always encourages responsible gambling."],
  ["Which leagues does Statlign cover?", "Statlign covers the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, the Champions League and many more competitions, plus NFL, NBA, MLB and NHL."],
];

function homeJsonLd(title: string, description: string) {
  const SITE = "https://statlign.com";
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE}/#webpage`,
      url: `${SITE}/`,
      name: title,
      description,
      inLanguage: "en",
      isPartOf: { "@id": `${SITE}/#website` },
      about: { "@id": `${SITE}/#organization` },
      publisher: { "@id": `${SITE}/#organization` },
      primaryImageOfPage: { "@type": "ImageObject", url: `${SITE}/og-image.jpg`, width: 1200, height: 630 },
      dateModified: new Date().toISOString().slice(0, 10),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    },
  ].map((g) => ({ type: "application/ld+json", children: JSON.stringify(g) }));
}
